import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { forgotPassword } from '../services/api';
import { getPasswordResetErrorMessage } from '../utils/errorMessages';
import { ROUTES } from '../constants';
import { ForgotPasswordFormData } from '../types';
import { validateEmail } from '../utils/validation';

export const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const response = await forgotPassword(formData);

    if (response.success) {
      setSuccessMessage('OTP sent successfully! Redirecting...');
      setTimeout(() => {
        navigate(ROUTES.RESET_PASSWORD, { state: { email: formData.email } });
      }, 1500);
    } else {
      setError(getPasswordResetErrorMessage(response.error));
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-3 sm:p-4">
      <Card className="w-full max-w-md">
        <button
          onClick={() => navigate(ROUTES.LOGIN)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 sm:mb-6 p-1 -ml-1 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span className="text-sm sm:text-base">Back to Login</span>
        </button>

        <div className="text-center mb-6 sm:mb-8">
          <Logo size="md" className="justify-center mb-3 sm:mb-4" />
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Mail className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Forgot Password?
          </h1>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
            Enter your email address and we'll send you a code to reset your password.
          </p>
        </div>

        {successMessage && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 text-xs sm:text-sm text-center">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs sm:text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Email Address"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            placeholder="your.email@example.com"
            required
            autoFocus
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            isLoading={loading}
            disabled={!formData.email}
          >
            Send Reset Code
          </Button>
        </form>
      </Card>
    </div>
  );
};
